import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, CircularProgress, Chip, Grid, Card, CardContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAgentUsageDetail, AgentUsageDetailKind, ToolDetail, UserDetail, QuestionDetail } from '../../hooks/useAgentUsageDetail';
import { formatDuration, formatToolName, truncate } from '../../utils/agentUsageFormat';
import TopBarChart from './TopBarChart';

const USER_BAR_COLOR = '#F57C00';

interface AgentUsageDetailModalProps {
  open: boolean;
  onClose: () => void;
  kind: AgentUsageDetailKind | null;
  identifier: string | null;
  label: string;
}

const StatChip = ({ label, value }: { label: string; value: string }) => (
  <Card sx={{ flex: 1, p: 1.5 }}>
    <CardContent sx={{ p: '0 !important' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight="bold">{value}</Typography>
    </CardContent>
  </Card>
);

const QuestionList = ({ title, questions }: { title: string; questions: Array<{ question: string; count: number }> }) => (
  <Box mt={2}>
    <Typography variant="subtitle2" fontWeight="bold" mb={1}>{title}</Typography>
    {questions.length > 0 ? (
      <Box display="flex" flexDirection="column" gap={1}>
        {questions.map((q, i) => (
          <Box key={`${q.question}-${i}`} display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" sx={{ flex: 1 }} title={q.question}>{truncate(q.question, 90)}</Typography>
            <Chip label={`${q.count}×`} size="small" color="primary" variant="outlined" />
          </Box>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">None in this range.</Typography>
    )}
  </Box>
);

const ToolList = ({ title, tools }: { title: string; tools: Array<{ tool: string; calls: number; totalTokens: number }> }) => (
  <Box mt={2}>
    <Typography variant="subtitle2" fontWeight="bold" mb={1}>{title}</Typography>
    {tools.length > 0 ? (
      <Box display="flex" flexDirection="column" gap={1}>
        {tools.map((t) => (
          <Box key={t.tool} display="flex" alignItems="center" gap={1.5}>
            <Typography variant="body2" sx={{ flex: 1 }}>{formatToolName(t.tool)}</Typography>
            <Chip label={`${t.calls} calls`} size="small" variant="outlined" />
            <Chip label={`${t.totalTokens.toLocaleString()} tokens`} size="small" variant="outlined" />
          </Box>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">None in this range.</Typography>
    )}
  </Box>
);

const TopUsersSection = ({ topUsers }: { topUsers: Array<{ userId: string; userName: string; calls: number; totalTokens: number }> }) => (
  <Box mt={2}>
    <Typography variant="subtitle2" fontWeight="bold" mb={1}>Top 5 Users</Typography>
    <TopBarChart
      data={topUsers.map((u) => ({ key: u.userId, name: u.userName, count: u.calls }))}
      color={USER_BAR_COLOR}
      tooltipSuffix="calls"
      emptyMessage="No user activity in this range."
    />
  </Box>
);

const AgentUsageDetailModal = ({ open, onClose, kind, identifier, label }: AgentUsageDetailModalProps) => {
  const { data, loading, error } = useAgentUsageDetail(kind, identifier, open);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold" sx={{ pr: 2 }}>{label}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error || !data || !kind ? (
          // !kind is deliberate, not just belt-and-suspenders: on close, the parent
          // resets kind to null before the Dialog's exit transition finishes, so
          // this still renders briefly with stale `data` from whichever kind was
          // last open. Without this guard the ternary below falls through to
          // QuestionDetailView, which reads `.byTool` — absent on a ToolDetail —
          // and crashes hard enough to take down the whole page's error boundary.
          <Typography color="error">{error || 'No data available'}</Typography>
        ) : kind === 'tool' ? (
          <ToolDetailView detail={data as ToolDetail} />
        ) : kind === 'user' ? (
          <UserDetailView detail={data as UserDetail} />
        ) : (
          <QuestionDetailView detail={data as QuestionDetail} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ToolDetailView = ({ detail }: { detail: ToolDetail }) => (
  <>
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}><StatChip label="Users Hit" value={detail.usersCount.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Calls" value={detail.calls.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Tokens" value={detail.totalTokens.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Time Used" value={formatDuration(detail.totalLatencyMs)} /></Grid>
    </Grid>
    <QuestionList title="Questions That Led Here" questions={detail.questions} />
    <TopUsersSection topUsers={detail.topUsers} />
  </>
);

const UserDetailView = ({ detail }: { detail: UserDetail }) => (
  <>
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}><StatChip label="Sessions" value={detail.sessionsCount.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Calls" value={detail.calls.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Tokens" value={detail.totalTokens.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Time Used" value={formatDuration(detail.totalLatencyMs)} /></Grid>
    </Grid>
    <ToolList title="Tools Called" tools={detail.byTool} />
    <QuestionList title="Top Questions Asked" questions={detail.topQuestions} />
  </>
);

const QuestionDetailView = ({ detail }: { detail: QuestionDetail }) => (
  <>
    <Grid container spacing={2}>
      <Grid item xs={6} sm={3}><StatChip label="Asked" value={`${detail.count.toLocaleString()}×`} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Users Asked" value={detail.usersCount.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Tokens" value={detail.totalTokens.toLocaleString()} /></Grid>
      <Grid item xs={6} sm={3}><StatChip label="Time Used" value={formatDuration(detail.totalLatencyMs)} /></Grid>
    </Grid>
    <ToolList title="Tools This Triggered" tools={detail.byTool} />
    <TopUsersSection topUsers={detail.topUsers} />
  </>
);

export default AgentUsageDetailModal;
